import buttonClasses from '@mui/material/Button/buttonClasses';
import { circularProgressClasses } from '@mui/material/CircularProgress';
import type { Components, Theme } from '@mui/material/styles';

export const insetStroke = (width: number, color: string) => ({
  outline: `${width}px solid ${color}`,
  outlineOffset: -width
});

export const gradientSpinner = (theme: Theme) => {
  const { background, border, icon } = theme.vars.palette.semantic;

  return {
    borderRadius: '50%',
    background: `conic-gradient(from 90deg, ${background['base-hover']}, ${icon.accent} 159.375deg, ${border['primary-hover']})`,
    // hex-ok: mask alpha, not a rendered color
    mask: 'radial-gradient(farthest-side, transparent calc(83.333% - 0.5px), #000 calc(83.333% + 0.5px))',
    [`& .${circularProgressClasses.svg}`]: {
      display: 'none'
    }
  };
};

export const buttonTheme: Components<Theme>['MuiButton'] = {
  defaultProps: {
    size: 'large',
    disableElevation: true,
    disableFocusRipple: true
  },
  styleOverrides: {
    root: ({ theme }) => {
      const { background, border, text } = theme.vars.palette.semantic;
      const focusRing = `rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`;

      return {
        minWidth: 'max-content',
        boxSizing: 'border-box',
        border: 0,
        boxShadow: 'none',
        [`&.${buttonClasses.disabled}`]: {
          border: 0
        },
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: 0,
        lineHeight: '20px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        transition: theme.transitions.create([
          'background-color',
          'box-shadow'
        ]),
        [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: {
          margin: 0,
          '& > :nth-of-type(1)': {
            fontSize: 20
          }
        },
        [`&.${buttonClasses.sizeSmall} .${buttonClasses.startIcon}, &.${buttonClasses.sizeSmall} .${buttonClasses.endIcon}`]:
          {
            '& > :nth-of-type(1)': {
              fontSize: 16
            }
          },
        [`& .${buttonClasses.loadingIndicator} .${circularProgressClasses.root}`]:
          {
            // CircularProgress sets its size as an inline style.
            width: '20px !important',
            height: '20px !important',
            ...gradientSpinner(theme)
          },
        [`&.${buttonClasses.sizeSmall} .${buttonClasses.loadingIndicator} .${circularProgressClasses.root}`]:
          {
            width: '16px !important',
            height: '16px !important'
          },
        [`&.${buttonClasses.loadingPositionStart}:not(.${buttonClasses.fullWidth}) .${buttonClasses.loadingIndicator}`]:
          {
            left: 'var(--Button-paddingX)'
          },
        [`&.${buttonClasses.loadingPositionEnd}:not(.${buttonClasses.fullWidth}) .${buttonClasses.loadingIndicator}`]:
          {
            right: 'var(--Button-paddingX)'
          },
        variants: [
          {
            props: { size: 'large' },
            style: {
              height: 42,
              gap: 8,
              '--Button-paddingX': '16px',
              padding: '0 var(--Button-paddingX)'
            }
          },
          {
            props: { size: 'medium' },
            style: {
              height: 36,
              gap: 8,
              '--Button-paddingX': '16px',
              padding: '0 var(--Button-paddingX)'
            }
          },
          {
            props: { size: 'small' },
            style: {
              height: 30,
              gap: 4,
              '--Button-paddingX': '12px',
              padding: '0 var(--Button-paddingX)'
            }
          },
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              backgroundColor: background.primary,
              color: text.inverse,
              '&:hover': {
                backgroundColor: background['primary-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                boxShadow: `0 0 0 4px ${focusRing}`
              },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: background.disabled,
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                backgroundColor: background.primary,
                color: text.inverse
              }
            }
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              backgroundColor: background.secondary,
              color: text.secondary,
              '&:hover': {
                backgroundColor: background['secondary-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                ...insetStroke(2, border.primary)
              },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: background.disabled,
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                backgroundColor: background.secondary,
                color: text.secondary
              }
            }
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              backgroundColor: background['error-primary'],
              color: text.inverse,
              '&:hover': {
                backgroundColor: background['error-primary-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                ...insetStroke(2, border.primary)
              },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: background.disabled,
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                backgroundColor: background['error-primary'],
                color: text.inverse
              }
            }
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              ...insetStroke(1, border.primary),
              backgroundColor: background.base,
              color: text.accent,
              '&:hover': {
                backgroundColor: background['base-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                ...insetStroke(2, border.primary),
                backgroundColor: background.tertiary
              },
              [`&.${buttonClasses.disabled}`]: {
                ...insetStroke(1, border.disabled),
                backgroundColor: background.base,
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                ...insetStroke(1, border.primary),
                color: text.accent
              }
            }
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              ...insetStroke(1, border.secondary),
              backgroundColor: background.base,
              color: text.caption,
              '&:hover': {
                backgroundColor: background['base-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                ...insetStroke(2, border.primary),
                backgroundColor: background.base
              },
              [`&.${buttonClasses.disabled}`]: {
                ...insetStroke(1, border.disabled),
                backgroundColor: background.base,
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                ...insetStroke(1, border.secondary),
                color: text.caption
              }
            }
          },
          {
            props: { variant: 'outlined', loading: true },
            style: {
              [`&.${buttonClasses.loading}`]: {
                backgroundColor: background.base
              }
            }
          },
          {
            props: { variant: 'text', color: 'primary' },
            style: {
              backgroundColor: 'transparent',
              color: text.accent,
              '&:hover': {
                backgroundColor: background['base-hover']
              },
              [`&.${buttonClasses.focusVisible}`]: {
                ...insetStroke(2, border.primary)
              },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: 'transparent',
                color: text.disabled
              },
              [`&.${buttonClasses.loading}`]: {
                color: text.accent
              }
            }
          },
          {
            props: { variant: 'text', size: 'small' },
            style: { gap: 2, '--Button-paddingX': '6px', margin: '0 -6px' }
          },
          {
            props: { loading: true, loadingPosition: 'center' },
            style: {
              [`&.${buttonClasses.loading}`]: { color: 'transparent' }
            }
          }
        ]
      };
    }
  }
};
